import React, { useState, useEffect } from 'react';
import { Check, Loader2, Building, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@snipit/core/context/AuthContext';
import { useOrganization } from '@snipit/core/hooks/useOrganization';
import { collection, query, where, getDocs, addDoc, onSnapshot, setDoc, doc } from 'firebase/firestore';
import { db } from '@snipit/core/services/firebase';
import ConsentWall from '@snipit/ui/components/ConsentWall';

export default function BillingView({ isEmbedded = false }) {
  const { userLicense, currentUser } = useAuth();
  const { orgMember, createOrganization } = useOrganization();
  const [loadingTier, setLoadingTier] = useState(null);
  const [stripeProducts, setStripeProducts] = useState({});
  const [billingCycle, setBillingCycle] = useState('month');

  // Org Setup state
  const [orgName, setOrgName] = useState('');
  const [isSettingUpOrg, setIsSettingUpOrg] = useState(false);

  const [consentGranted, setConsentGranted] = useState(false);
  const currentTierId = userLicense?.tier || 'free';
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('webPolicyConsent') === 'true') {
      setConsentGranted(true);
    }
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(collection(db, 'products'), where('active', '==', true));
        const querySnapshot = await getDocs(q);
        const productsMap = {};
        
        for (const productDoc of querySnapshot.docs) {
          const data = productDoc.data();
          // The extension sometimes maps it directly to data.role
          const role = data.role || data.metadata?.firebaseRole || data.stripe_metadata_firebaseRole;
          
          if (role) {
            // Remove any trailing spaces the user might have accidentally typed
            const cleanRole = role.trim();
            const priceSnap = await getDocs(query(collection(db, 'products', productDoc.id, 'prices'), where('active', '==', true)));
            const prices = priceSnap.docs.map(p => ({ id: p.id, ...p.data() }));
            productsMap[cleanRole] = { id: productDoc.id, ...data, prices };
          }
        }
        console.log("Successfully fetched Stripe Products Map:", productsMap);
        setStripeProducts(productsMap);
      } catch (error) {
        console.error("Error fetching products:", error);
        alert(`Error fetching products from database: ${error.message}`);
      }
    };
    fetchProducts();
  }, []);

  const getDisplayPrice = (tierId, fallback) => {
    const product = stripeProducts[tierId];
    if (product && product.prices && product.prices.length > 0) {
      // Find price matching the current billing cycle
      const price = product.prices.find(p => p.interval === billingCycle) || product.prices[0];
      if (price && price.unit_amount !== undefined) {
        if (!price.interval) {
          return `$${(price.unit_amount / 100).toFixed(0)}`;
        }
        return `$${(price.unit_amount / 100).toFixed(0)}/${billingCycle === 'month' ? 'mo' : 'yr'}`;
      }
    }
    return fallback;
  };

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      price: getDisplayPrice('free', '$0'),
      description: 'Perfect for individual developers getting started.',
      features: ['Local Storage Only', 'Basic Snippet Vaults', 'Standard IDE Themes'],
    },
    {
      id: 'premium',
      name: 'Pro',
      price: getDisplayPrice('premium', billingCycle === 'month' ? '$10/mo' : '$70/yr'),
      description: 'For professionals who need cross-device syncing.',
      features: ['Cloud Sync (Firebase)', 'AI Prompts Vault', 'Developer Board Access'],
    },
    {
      id: 'enterprise',
      name: 'Enterprise (Single Dev)',
      price: getDisplayPrice('enterprise', billingCycle === 'month' ? '$30/mo' : '$250/yr'),
      description: 'Advanced AI & security for independent power users.',
      features: ['Advanced AI Prompts', 'Priority Support', 'Cloud Sync'],
    },
    {
      id: 'enterprise_org',
      name: 'Enterprise Teams',
      price: getDisplayPrice('enterprise_org', billingCycle === 'month' ? '$50/mo' : '$500/yr'),
      description: 'For organizations sharing knowledge securely.',
      features: ['Organization Workspaces', 'Team Sharing (RBAC)', 'SSO Authentication', 'Dedicated Success Manager'],
    }
  ];

  const handleCheckout = async (tierId) => {
    if (!currentUser) {
      if (tierId === 'free') {
        navigate('/home');
      } else {
        navigate('/login');
      }
      return;
    }
    
    if (tierId === 'free') {
      navigate('/home');
      return;
    }

    setLoadingTier(tierId);
    try {
      const product = stripeProducts[tierId];
      if (!product || !product.prices || product.prices.length === 0) {
        const availableKeys = Object.keys(stripeProducts).join(', ') || 'None';
        alert(`Products are still syncing from Stripe. Please try again in a few seconds!\n\nDebug Info:\nTier requested: ${tierId}\nAvailable Roles in DB: ${availableKeys}\nFound Product: ${!!product}\nFound Prices: ${product?.prices?.length || 0}`);
        setLoadingTier(null);
        return;
      }
      
      // Match the price ID to the selected billing cycle
      const priceObj = product.prices.find(p => p.interval === billingCycle) || product.prices[0];
      const priceId = priceObj.id;
      
      const docRef = await addDoc(collection(db, 'customers', currentUser.uid, 'checkout_sessions'), {
        price: priceId,
        success_url: window.location.origin,
        cancel_url: window.location.origin,
      });
      
      onSnapshot(docRef, (snap) => {
        const data = snap.data();
        if (data?.error) {
          alert(`An error occurred: ${data.error.message}`);
          setLoadingTier(null);
        }
        if (data?.url) {
          window.location.assign(data.url);
        }
      });
    } catch (error) {
      console.error("Checkout error:", error);
      setLoadingTier(null);
    }
  };

  const handleSetupOrganization = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) return alert("Please enter an organization name");
    
    setIsSettingUpOrg(true);
    try {
      // Use createOrganization hook
      await createOrganization(orgName.trim());

      alert("Organization setup successfully! The Teams and Organization tabs are now available in your sidebar.");
    } catch (err) {
      console.error(err);
      alert("Failed to setup organization. Please try again.");
    }
    setIsSettingUpOrg(false);
  };

  if (!currentUser && !consentGranted) {
    return <ConsentWall onAccept={() => { localStorage.setItem('webPolicyConsent', 'true'); setConsentGranted(true); }} onDecline={() => navigate('/')} />;
  }

  if (currentTierId === 'enterprise_org' && !orgMember) {
    return (
      <div className="w-full h-full bg-[#121212] flex items-center justify-center p-6 text-gray-200">
        <div className="bg-[#1a1a1a] p-8 rounded-2xl border border-indigo-900/50 shadow-2xl max-w-lg w-full">
          <div className="flex justify-center mb-6">
            <div className="bg-indigo-500/20 p-4 rounded-full border border-indigo-500/30">
              <Building className="w-12 h-12 text-indigo-400" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white text-center mb-2">Welcome to Enterprise Teams!</h2>
          <p className="text-gray-400 text-center mb-8">
            Thank you for purchasing the Enterprise Teams license. To get started, please setup your initial organization workspace.
          </p>
          <form onSubmit={handleSetupOrganization} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Organization Name</label>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full bg-[#121212] border border-gray-800 rounded-xl p-4 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                placeholder="e.g. Acme Corp"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isSettingUpOrg}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {isSettingUpOrg ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Complete Setup'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={isEmbedded ? "" : "p-8 h-full overflow-y-auto"}>
      {!currentUser && !isEmbedded && (
        <button onClick={() => navigate('/')} className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </button>
      )}
      <div className="flex justify-between items-start mb-8">
        <div>
          {!isEmbedded && <h1 className="text-3xl font-bold text-white mb-2">Billing & Licenses</h1>}
          {!isEmbedded && <p className="text-gray-400">Manage your subscription and billing details.</p>}
        </div>
        {currentUser && (
          <a
            href="https://billing.stripe.com/p/login/14A14nbuE4UBeH970ffYY00"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gray-800 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors border border-gray-700 flex items-center gap-2"
          >
            Manage Billing
          </a>
        )}
      </div>

      {userLicense?.status === 'past_due' && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-8">
          <h3 className="text-red-400 font-bold mb-1">Payment Action Required</h3>
          <p className="text-gray-300 text-sm">Your last payment failed. Please update your billing method to keep your Cloud Sync features active.</p>
        </div>
      )}

      <div className="flex justify-center mb-8">
        <div className="bg-[#1a1a1a] border border-gray-800 p-1 rounded-xl inline-flex relative">
          <button
            onClick={() => setBillingCycle('month')}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              billingCycle === 'month' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-white'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('year')}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              billingCycle === 'year' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:text-white'
            }`}
          >
            Yearly
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              billingCycle === 'year' ? 'bg-emerald-500 text-white' : 'bg-emerald-500/20 text-emerald-400'
            }`}>
              Save 20%
            </span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
        {tiers.map((tier) => {
          const isCurrent = currentTierId === tier.id;
          
          return (
            <div key={tier.name} className={`bg-[#1a1a1a] border ${isCurrent ? 'border-blue-500 shadow-lg shadow-blue-500/10' : 'border-gray-800'} rounded-xl p-6 flex flex-col relative overflow-hidden`}>
              {isCurrent && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  ACTIVE
                </div>
              )}
              
              <h3 className="text-2xl font-bold text-white mb-2 mt-2">{tier.name}</h3>
              <div className="text-3xl font-bold text-gray-200 mb-4">{tier.price}</div>
              <p className="text-gray-400 text-sm mb-6 flex-1">{tier.description}</p>
              
              <ul className="space-y-3 mb-8">
                {tier.features.map(feature => (
                  <li key={feature} className="flex items-center text-sm text-gray-300">
                    <Check className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleCheckout(tier.id)}
                disabled={(isCurrent && currentUser) || loadingTier === tier.id}
                className={`w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center ${
                  isCurrent && currentUser ? 'bg-gray-800 text-gray-400 cursor-not-allowed' : 
                  'bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-900/20'
                }`}
              >
                {loadingTier === tier.id ? <Loader2 className="w-5 h-5 animate-spin" /> : 
                 (isCurrent && currentUser) ? 'Current Plan' : 
                 (tier.id === 'free' && !currentUser) ? 'Get Started' : 'Upgrade to ' + tier.name}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
